<template>
    <div class="q-gutter-sm">
        <div v-show="isEventListView">
            <!-- table of events -->
            <q-table
                @request="onRequest"
                :rows="table.data"
                :columns="table.cols"
                v-model:pagination="table.pagination"
                :rows-per-page-options="[table.pagination.rowsPerPage]"
                class="table q-pa-xs"
                :class="$q.screen.gt.md ? 'no-shadow border-grey' : ''"
                table-class="events-table"
                :no-data-label="
                    $t(
                        'There is no quality event in the date range you provided.'
                    )
                "
                :grid="$q.screen.lt.lg"
                row-key="id"
                binary-state-sort
            >
                <!-- top bar -->
                <template v-slot:top>
                    <div class="col-12 q-my-sm q-px-md">
                        <div class="text-h4 text-weight-medium">
                            {{ $t('Quality Events') }}
                        </div>
                        <div class="page-description">
                            {{
                                $t(
                                    'Track and analyze quality events for {facilityName}.',
                                    {
                                        facilityName: facility?.name
                                    }
                                )
                            }}
                        </div>
                    </div>

                    <!-- date time pickers (second row) -->
                    <div class="row full-width q-mb-sm q-col-gutter-sm q-px-sm">
                        <date-time-picker
                            id="start-period-input"
                            v-model="dateTimePicker.start"
                            :dense="true"
                            :label="$t('Start')"
                            :readonly="true"
                            class="col-md-3 col-xs-12 col-sm-6"
                        />

                        <date-time-picker
                            id="end-period-input"
                            v-model="dateTimePicker.end"
                            :dense="true"
                            :label="$t('End')"
                            :readonly="true"
                            class="col-md-3 col-xs-12 col-sm-6"
                        />

                        <div class="col-md-3 col-xs-12">
                            <q-btn
                                id="apply-button"
                                @click="
                                    onRequest({
                                        pagination: table.pagination
                                    })
                                "
                                :label="$t('Apply')"
                                :class="{
                                    'full-width q-my-md': $q.screen.lt.md
                                }"
                                color="accent"
                                text-color="white"
                                style="height: 39px"
                            ></q-btn>
                        </div>
                    </div>

                    <!-- filters and buttons (third row)-->
                    <div class="row full-width q-col-gutter-sm q-px-sm q-mb-md">
                        <remote-select
                            id="asset-select"
                            :model-value="eventFilter.assets"
                            @update:model-value="eventFilter.assets = $event"
                            :label="$t('Asset')"
                            :baseURL="'assets/'"
                            :urlParams="{
                                facility: facilityId,
                                ordering: 'name'
                            }"
                            class="col-lg-3 col-md-6 col-sm-6 col-xs-12"
                            dense
                            outlined
                            searchable
                            clearable
                            @clear="eventFilter.assets = []"
                            :multiple="true"
                        />
                        <table-select
                            id="product-select"
                            v-model="eventFilter.product"
                            @confirmSelection="
                                eventFilter.product =
                                    productSelectProps.selection
                            "
                            :baseURL="productSelectProps.url"
                            :queryParams="productSelectProps.queryParams"
                            :label="productSelectProps.label"
                            :dialogTitle="productSelectProps.dialogTitle"
                            :searchLabel="productSelectProps.searchLabel"
                            :dense="true"
                            row-key="id"
                            class="col-lg-3 col-md-6 col-sm-6 col-xs-12"
                        >
                            <template v-slot:item="data">
                                <product-card
                                    @select="
                                        select(
                                            data.props.row,
                                            productSelectProps
                                        )
                                    "
                                    :name="data.props.row.name"
                                    :code="data.props.row.code"
                                    :description="data.props.row.description"
                                    :unit-measure="data.props.row.unit_measure"
                                    :unit-packing="data.props.row.unit_packing"
                                    :isSelected="
                                        productSelectProps.selection &&
                                        productSelectProps.selection.value ===
                                            data.props.row.id
                                    "
                                    :isSelectable="true"
                                />
                            </template>
                        </table-select>
                        <defect-table-select
                            v-if="defectSelectProps.url"
                            id="defect-select"
                            v-model="eventFilter.defect"
                            @confirmSelection="
                                eventFilter.defect = defectSelectProps.selection
                            "
                            :baseURL="defectSelectProps.url"
                            :queryParams="defectSelectProps.queryParams"
                            :label="defectSelectProps.label"
                            :dialogTitle="defectSelectProps.dialogTitle"
                            :searchLabel="defectSelectProps.searchLabel"
                            :facilityId="defectSelectProps.facilityId"
                            :dense="true"
                            row-key="id"
                            class="col-lg-3 col-md-6 col-sm-6 col-xs-12"
                        >
                            <template v-slot:item="data">
                                <defect-card
                                    @select="
                                        select(
                                            data.props.row,
                                            defectSelectProps
                                        )
                                    "
                                    :name="data.props.row.name"
                                    :code="data.props.row.code"
                                    :description="data.props.row.description"
                                    :isSelected="
                                        defectSelectProps.selection &&
                                        defectSelectProps.selection.value ===
                                            data.props.row.id
                                    "
                                    :isSelectable="true"
                                />
                            </template>
                        </defect-table-select>
                        <div class="col-lg-3 col-md-6 col-sm-6 col-xs-12">
                            <div
                                :class="{
                                    'float-right': $q.screen.gt.sm
                                }"
                            >
                                <q-btn
                                    @click="refreshAndResetFilters"
                                    id="refresh-list-button"
                                    class="bg-white"
                                    :class="{
                                        'full-width': $q.screen.lt.md
                                    }"
                                    :label="$t('Refresh')"
                                    icon="fas fa-sync"
                                    color="accent"
                                    outline
                                >
                                    <q-tooltip>{{ $t('Refresh') }}</q-tooltip>
                                </q-btn>
                                <!-- 
                                This button will be used in the future to download the quality events
                                <q-btn
                                    @click="showDownloadEventsDialog = true"
                                    :label="$t('Download')"
                                    color="accent"
                                    text-color="white"
                                    class="q-ml-sm"
                                    :class="[
                                        $q.screen.xs
                                            ? 'full-width  q-mt-sm q-mb-sm'
                                            : '',
                                        $q.screen.lt.lg
                                            ? 'half-width '
                                            : 'q-mx-sm'
                                    ]"
                                >
                                    <q-tooltip>
                                        {{ $t('Download quality events') }}
                                    </q-tooltip>
                                </q-btn> -->
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Status column -->
                <template v-slot:body-cell-status="props">
                    <q-td :props="props">
                        <div>
                            <q-chip
                                class="event-table-status"
                                style="font-weight: 500"
                                outline
                                color="darker-yellow"
                                :label="$t('Quality')"
                                square
                            >
                            </q-chip>
                        </div>
                    </q-td>
                </template>

                <!-- Defect column -->
                <template v-slot:body-cell-defect="props">
                    <q-td :props="props">
                        <span v-if="props.row.defectName">
                            {{ props.row.defectName }}
                        </span>
                        <missing-defect-label
                            v-else
                            v-model="missingDefectProps.selection"
                            :eventId="props.row.id"
                            :assetId="props.row.assetId"
                            @refreshList="refreshList()"
                            @clearSelection="
                                missingDefectProps.selection = null
                            "
                            :baseURL="`assets/${props.row.assetId}/defects/`"
                            :queryParams="missingDefectProps.queryParams"
                            :label="missingDefectProps.label"
                            :dialogTitle="missingDefectProps.dialogTitle"
                            :searchLabel="missingDefectProps.searchLabel"
                        >
                            <template v-slot:item="data">
                                <defect-card
                                    @select="
                                        select(
                                            data.props.row,
                                            missingDefectProps
                                        )
                                    "
                                    :name="data.props.row.name"
                                    :code="data.props.row.code"
                                    :description="data.props.row.description"
                                    :isSelected="
                                        missingDefectProps.selection &&
                                        missingDefectProps.selection.value ===
                                            data.props.row.id
                                    "
                                    :isSelectable="true"
                                />
                            </template>
                        </missing-defect-label>
                    </q-td>
                </template>

                <!-- Actions column -->
                <template v-slot:body-cell-actions="props">
                    <q-td :props="props">
                        <div class="row">
                            <!-- "Update event" button -->
                            <q-btn
                                @click="showEventDetail(props.row)"
                                :disable="$userCannot('edit', 'quality_events')"
                                class="action-button"
                                color="accent"
                                icon="fas fa-edit"
                                id="update-quality-event-button"
                                :size="$q.screen.gt.sm ? 'sm' : 'xs'"
                                outline
                            />
                            <q-btn
                                @click="
                                    $router.push({
                                        name: 'asset_timeline',
                                        params: {
                                            assetId: props.row.assetId
                                        },
                                        query: {
                                            datetime: props.row.start
                                        }
                                    })
                                "
                                class="action-button"
                                icon="fas fa-history"
                                color="accent"
                                :size="$q.screen.gt.sm ? 'sm' : 'xs'"
                                no-caps
                                outline
                            >
                                <q-tooltip>
                                    {{
                                        $t(
                                            'Go to the phase timeline of this asset'
                                        )
                                    }}
                                </q-tooltip>
                            </q-btn>
                        </div>
                    </q-td>
                </template>

                <!-- Card style for grid mode -->
                <template v-slot:item="props">
                    <div class="full-width q-pa-xs">
                        <quality-event-card
                            @save="getEvents"
                            @refreshList="refreshList()"
                            :facilityId="facilityId"
                            :eventId="props.row.id"
                            :defectId="props.row.defectId"
                            :defectName="props.row.defectName"
                            :assetId="props.row.assetId"
                            :assetName="getAssetName(props.row.assetId)"
                            :start="props.row.start"
                            :end="props.row.end === null ? null : props.row.end"
                            :productName="props.row.productName"
                            :rejectedProductCount="
                                props.row.rejectedProductCount
                            "
                            :grossProductCount="props.row.grossProductCount"
                        />
                    </div>
                </template>
            </q-table>
        </div>

        <!-- Detail view -->
        <div v-if="!isEventListView">
            <quality-event-detail
                @return="onEventDetailReturn"
                @save="getEvents"
                :editEvent="true"
                :facilityId="selectedEvent.facilityId"
                :eventId="selectedEvent.eventId"
                :productId="selectedEvent.product"
                :productName="selectedEvent.productName"
                :assetId="selectedEvent.assetId"
                :assetName="selectedEvent.assetName"
                :start="selectedEvent.start"
                :end="selectedEvent.end"
                :defectId="selectedEvent.defect"
                :defectName="selectedEvent.defectName"
                :rejectedProductCount="selectedEvent.rejectedProductCount"
                :grossProductCount="selectedEvent.grossProductCount"
            />
        </div>
    </div>
</template>

<script>
import DateTimePicker from '@components/DateTimePicker.vue';
import DefectCard from '@components/DefectCard.vue';
import DefectTableSelect from '@components/DefectTableSelect.vue';
import MissingDefectLabel from '@components/MissingDefectLabel.vue';
import ProductCard from '@components/ProductCard.vue';
import QualityEventCard from '@components/QualityEventCard.vue';
import RemoteSelect from '@components/RemoteSelect.vue';
import TableSelect from '@components/TableSelect.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { getLocaleFormat } from '@models/timeformats.js';
import { mapGetters } from 'vuex';
import QualityEventDetail from './QualityEventDetail.vue';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'QualityEventsList',
    components: {
        ProductCard,
        DateTimePicker,
        RemoteSelect,
        TableSelect,
        QualityEventDetail,
        DefectTableSelect,
        DefectCard,
        QualityEventCard,
        MissingDefectLabel
    },
    mixins: [BreadcrumbsManager, UpdateURL],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    async created() {
        // init time in the datetime picker
        this.initDateTimeRange();
        // set up the breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility?.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility?.id
                    }
                }
            },
            {
                label: this.$t('Quality Events')
            }
        ]);
        // fetch data from the server
        await this.onRequest({
            pagination: this.table.pagination
        });
    },
    data() {
        return {
            // models
            baseURL: 'quality_events/',
            url: 'quality_events/',
            events: [],
            // datetime
            dateTimePicker: {
                start: '',
                end: ''
            },
            selected: [],
            isEventListView: true,

            table: {
                data: [],
                cols: [
                    {
                        name: 'status',
                        field: 'status',
                        label: 'Status',
                        required: true,
                        align: 'left',
                        sortable: 'false'
                    },
                    {
                        name: 'asset',
                        required: true,
                        label: this.$t('Asset'),
                        field: row => this.getAssetName(row.assetId),
                        align: 'left',
                        sortable: false
                    },
                    {
                        name: 'product',
                        required: true,
                        label: this.$t('Product'),
                        field: 'productName',
                        align: 'left',
                        sortable: false
                    },
                    {
                        name: 'defect',
                        required: true,
                        label: this.$t('Defect'),
                        field: 'defectName',
                        align: 'left',
                        sortable: false
                    },
                    {
                        name: 'start',
                        label: this.$t('Start Time'),
                        field: row =>
                            this.formatDate(row.start, this.userLocale),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'end',
                        label: this.$t('End Time'),
                        field: row => this.formatDate(row.end, this.userLocale),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'rejectedProductCount',
                        label: this.$t('Rejected Products'),
                        field: 'rejectedProductCount',
                        align: 'left',
                        sortable: false
                    },
                    {
                        name: 'grossProductCount',
                        label: this.$t('Total Products'),
                        field: 'grossProductCount',
                        align: 'left',
                        sortable: false
                    },
                    {
                        name: 'actions',
                        label: this.$t('Actions'),
                        field: 'actions',
                        align: 'left'
                    }
                ],
                pagination: {
                    sortBy: 'start',
                    descending: true,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                }
            },
            eventFilter: {
                assets: [],
                product: null,
                defect: null
            },

            // selectors
            productSelectProps: {
                url: '/products/',
                queryParams: {
                    facility: this.facilityId,
                    ordering: 'name'
                },
                label: this.$t('Product'),
                dialogTitle: this.$t('Select a product'),
                searchLabel: this.$t('Search product'),
                selection: null
            },
            defectSelectProps: {
                url: 'defects/',
                queryParams: { order: 'name' },
                label: this.$t('Defect'),
                dialogTitle: this.$t('Select a Defect'),
                searchLabel: this.$t('Search a defect'),
                selection: null,
                facilityId: null
            },
            // selector for missing defect label
            missingDefectProps: {
                queryParams: { ordering: 'name' },
                label: this.$t('Defect'),
                dialogTitle: this.$t('Select a Defect'),
                searchLabel: this.$t('Search a defect'),
                selection: null
            },
            // event selected for edit or splitting
            selectedEvent: {
                facilityId: null,
                eventId: null,
                productId: null,
                productName: null,
                defectId: null,
                defectName: null,
                assetId: null,
                assetName: null,
                start: null,
                end: null,
                rejectedProductCount: null,
                grossProductCount: null
            }
        };
    },
    watch: {
        eventFilter: {
            deep: true,
            immediate: true,
            async handler() {
                // send request to server
                if (
                    !(
                        this.dateTimePicker.start === '' &&
                        this.dateTimePicker.end === ''
                    )
                ) {
                    // send query with first page
                    this.table.pagination['page'] = 1;
                    await this.onRequest({
                        pagination: this.table.pagination
                    });
                }
            }
        }
    },
    computed: {
        ...mapGetters({
            getAssetName: 'facilities/getAssetName'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get selected facility.
         * @return {Object} - facility.
         */
        facility() {
            return this.$store.getters['facilities/getFacility'](
                this.facilityId
            );
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @param {String} locale
         * @return {String} datetime in given locale.
         */
        formatDate(datetime, locale) {
            return getLocaleFormat(datetime, locale);
        },
        /**
         * Refetch the results from the server.
         */
        async refreshList() {
            // set current date and time to "now"
            this.datetimePicker = new Date().toISOString();

            // fetch data from the server
            await this.onRequest({
                pagination: this.table.pagination,
                filter: ''
            });
        },
        /**
         * Fetch event data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         * @param {Object} props.pagination
         */
        async onRequest(props) {
            // update pagination object
            this.table.pagination = props.pagination;
            // get current pagination state attributes
            const { descending, page, sortBy } = props.pagination;

            let urlParams = {
                facility: this.facilityId,
                ordering: descending ? `-${sortBy}` : `${sortBy}`,
                page: page,
                start__lte: this.dateTimePicker.end,
                end__gte: this.dateTimePicker.start
            };
            if (this.eventFilter.assets !== null) {
                if (this.eventFilter.assets.length > 0) {
                    urlParams['asset__in'] = this.eventFilter.assets.map(
                        asset => asset.value
                    );
                }
            }
            if (this.eventFilter.product !== null) {
                urlParams['product'] = this.eventFilter.product.value;
            }

            if (this.eventFilter.defect !== null) {
                urlParams['defect'] = this.eventFilter.defect.value;
            }

            // update URL
            this.url = this.updateURL({
                baseURL: this.baseURL,
                params: urlParams
            });
            // clear existing data
            this.events = [];
            this.table.data = [];

            // fetch events from the server
            await this.getEvents();
        },
        /**
         * Get events from the server.
         */
        async getEvents() {
            // begin loading
            this.beginLoading(`${this.$t('Loading events')}...`);

            const errorMessage = this.$t(
                'Failed to load events. Please try again later'
            );
            try {
                // send GET HTTPS request
                const response = await this.$axios.get(this.url);
                if (response.status === 200) {
                    // clear existing data
                    this.table.data = [];
                    // get raw event response
                    const rawEvents = response.data.results;
                    // format event data
                    rawEvents.forEach(rawEvent => {
                        const event = {
                            id: rawEvent['id'],
                            eventUUID: rawEvent['uuid'],
                            assetId: rawEvent['asset'],
                            defectId: rawEvent['defect'],
                            defectName: rawEvent['defect_name'],
                            start: rawEvent['start'],
                            end: rawEvent['end'],
                            productId: rawEvent['product'],
                            productName: rawEvent['product_name'],
                            grossProductCount: rawEvent['gross_production'],
                            rejectedProductCount:
                                rawEvent['rejected_production']
                        };
                        this.table.data.push(event);
                    });
                    // update total events count
                    this.table.pagination.rowsNumber = response.data.count;
                } else {
                    this.$notifyError(errorMessage);
                }
            } catch (error) {
                console.error(error);
                this.$notifyError(errorMessage, error);
            } finally {
                // stop loading
                this.stopLoading();
            }
        },

        /**
         * Refreshes the data and resets the filters.
         */
        async refreshAndResetFilters() {
            // reset form fields
            this.initDateTimeRange();
            this.eventFilter.assets = [];
            this.eventFilter.product = null;
            this.eventFilter.defect = null;
            await this.onRequest({
                pagination: this.table.pagination
            });
        },
        /************************
         * Event Detail methods *
         ************************/
        /**
         * Show the event detail page.
         * @param {Object} row - Event from table row.
         */
        showEventDetail(row) {
            this.selectedEvent = {
                facilityId: parseInt(this.facilityId),
                eventId: row.id,
                productId: row.productId,
                productName: row.productName,
                defectId: row.defectId,
                defectName: row.defectName,
                assetId: row.assetId,
                assetName: this.getAssetName(row.assetId),
                start: row.start,
                end: row.end,
                rejectedProductCount: row.rejectedProductCount,
                grossProductCount: row.grossProductCount
            };
            this.isEventListView = false;
        },
        clearSelectedEvent() {
            this.selectedEvent = {
                facilityId: null,
                eventId: null,
                productId: null,
                productName: null,
                defectId: null,
                defectName: null,
                assetId: null,
                assetName: null,
                start: null,
                end: null,
                rejectedProductCount: null,
                grossProductCount: null
            };
        },

        async onEventDetailReturn() {
            this.isEventListView = true;
            this.clearSelectedEvent();
            await this.getEvents();
        },
        /****************************
         * Datetime related methods *
         ****************************/

        /**
         * init the time range in ISO 8601 format
         * i.e. start: "2020-03-03T20:35:39.700Z"; end: '2020-03-03T21:35:39.700Z'
         */
        initDateTimeRange() {
            // init start Date string at 24 hours before now
            this.dateTimePicker.start = new Date(
                new Date().getTime() - 24 * 60 * 60 * 1000
            ).toISOString();
            // init end Date string at now
            this.dateTimePicker.end = new Date().toISOString();
        },
        /**
         * Select/ deselect defect and products in table-select component
         * @param {Object} row - Event from table row.
         * @param {Object} props - Table selection props (productTableSelect, MissingDefectLabel)
         */
        select(row, props) {
            if (props.selection && props.selection.value === row.id) {
                props.selection = null;
            } else {
                props.selection = {
                    label: row.name,
                    value: row.id
                };
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.table {
    height: 100%;
}

// align buttons to the right end of the page
.buttons {
    padding-right: 0px;
}

.action-button {
    padding: 12px;
    font-size: 12px;
    margin: 5px;
}

.events-table {
    height: 80vh;
}

// remove table top padding
:deep(.q-table__top) {
    padding: 0px;
}

// remove q-input bottom padding due to table-select validator
:deep(.q-field--with-bottom) {
    padding: 0px;
}

:deep(.events-table) {
    // table to fill entire panel
    height: 618px;
    max-height: none;
}

:deep(.events-table-cell) {
    font-size: 14px;
}

:deep(.q-table__container > div:first-child) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
}

.events-table-actions-col {
    width: 100px;
}

.events-table-button :deep(.q-btn__wrapper) {
    padding: 6px;
    font-size: 12px;
}

.events-table-status {
    font-size: 13px;
    padding: 4px;
}
.half-width {
    width: calc(50% - 5px);
}
</style>
